// ── Subscription pricing ─────────────────────────────────────────────────────
// First year (setup + year one): €999 for 3 users (owner + 2). Every year
// after that: €300 renewal for the same 3 users. Each active user above
// the included ones pays €100 per year, in the first year and at every
// renewal. A user added mid year is simply counted at the next renewal.
// Each company can override all four prices (Admin Portal › Add/Edit
// Company). Payments record their own breakdown (Payment.kind / feeAmount /
// extraUsers…), so changing a price never rewrites an old payment.

import type { Business, Payment, PaymentKind, BillingCurrency } from '../types';
import { COUNTRY_CURRENCIES, currencyOfCountry } from './countryCurrency';

// ── Currencies DiaWolt bills in ─────────────────────────────────────────────
// A company is billed in its Country's currency (Business.billingCurrency,
// any ISO code, EUR when not set; currencyOfCountry fills it). The standard
// plan is set in € (PLAN_EUR); another currency gets it converted at that
// day's rate and rounded, ONCE, when the company is added or its currency
// changes, then saved on the company (editable). Payments keep their own
// currency and the € rate of the day they were logged (rateToEur).

/** The standard plan in €. Change these to change the price list. */
export const PLAN_EUR = { firstYear: 999, renewal: 300, extraUser: 100 };

/** € per one unit of each currency ("1 INR = €0.0108"). Today's rates are
 *  fetched (fetchLiveRates); these are only the fallback. */
export type RatesToEur = Record<string, number>;
export const DEFAULT_RATES_TO_EUR: RatesToEur = { EUR: 1, USD: 0.92, AED: 0.25, INR: 0.011 };

export { currencyOfCountry };
/** Every billing currency on offer: one per country's currency. */
export const BILLING_CURRENCY_CODES: string[] = COUNTRY_CURRENCIES;

const currencyNames = (() => {
  try { return new Intl.DisplayNames(['en'], { type: 'currency' }); } catch { return null; }
})();
/** "INR · Indian Rupee" for a list. */
export function currencyLabel(code: string): string {
  const name = currencyNames?.of(code);
  return name && name !== code ? `${code} · ${name}` : code;
}

export const billingCurrencyOf = (b?: Pick<Business, 'billingCurrency'> | null): BillingCurrency =>
  b?.billingCurrency || 'EUR';
export const paymentCurrencyOf = (p: Pick<Payment, 'currency'>): BillingCurrency =>
  typeof p.currency === 'string' && /^[A-Z]{3}$/.test(p.currency) ? p.currency : 'EUR';

/** "€1,234", "$1,234", "AED 1,234", "₹1,23,456" (Indian grouping for ₹). */
export function fmtBill(amount: number, cur: BillingCurrency = 'EUR'): string {
  try {
    return new Intl.NumberFormat(cur === 'INR' ? 'en-IN' : 'en-US', {
      style: 'currency', currency: cur, minimumFractionDigits: 0, maximumFractionDigits: 2,
    }).format(amount ?? 0);
  } catch {
    return `${cur} ${(amount ?? 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
  }
}
/** "€1" style symbol for a field label ("Amount (₹)"). */
export function curSymbol(cur: BillingCurrency): string {
  return fmtBill(0, cur).replace(/[\d.,\s]/g, '') || cur;
}
/** Several currencies side by side, € first: "€4,500 · ₹1,32,000". Empty → "€0". */
export function fmtBillMulti(byCur: Partial<Record<BillingCurrency, number>>): string {
  const parts = Object.keys(byCur)
    .filter(c => Math.abs(byCur[c] ?? 0) > 0.005)
    .sort((a, b) => (a === 'EUR' ? -1 : b === 'EUR' ? 1 : a.localeCompare(b)))
    .map(c => fmtBill(byCur[c]!, c));
  return parts.length ? parts.join(' · ') : fmtBill(0, 'EUR');
}
/** The amount in €; 0 when no rate is known for that currency. */
export function toEur(amount: number, cur: BillingCurrency, rates: RatesToEur): number {
  return amount * (cur === 'EUR' ? 1 : rates[cur] ?? DEFAULT_RATES_TO_EUR[cur] ?? 0);
}
/** "≈ €999" beside an amount in another currency; '' for € or no rate. */
export function eurApprox(amount: number, cur: BillingCurrency, rates: RatesToEur, rateToEur?: number): string {
  if (cur === 'EUR') return '';
  const eur = rateToEur ? amount * rateToEur : toEur(amount, cur, rates);
  return eur > 0 ? `≈ ${fmtBill(Math.round(eur), 'EUR')}` : '';
}

/** Three significant figures: 92,026 → 92,000; 1,078.9 → 1,080. */
function niceRound(x: number): number {
  if (!(x > 0)) return 0;
  const step = 10 ** Math.max(0, Math.floor(Math.log10(x)) - 2);
  return Math.round(x / step) * step;
}
/** The € plan in another currency at these rates (the € plan if no rate). */
export function planFor(cur: BillingCurrency, rates: RatesToEur): typeof PLAN_EUR {
  const r = cur === 'EUR' ? 1 : rates[cur] ?? DEFAULT_RATES_TO_EUR[cur];
  if (!r) return PLAN_EUR;
  return {
    firstYear: niceRound(PLAN_EUR.firstYear / r),
    renewal:   niceRound(PLAN_EUR.renewal / r),
    extraUser: niceRound(PLAN_EUR.extraUser / r),
  };
}

// ── Today's rates, fetched ──────────────────────────────────────────────────
// From open.er-api.com (free, no key, every currency), else the jsDelivr
// currency-api mirror. Cached per browser for the day.
const LIVE_RATES_KEY = 'admin-live-rates';
export interface LiveRates { date: string; rates: RatesToEur }

/** Units per € ({ INR: 92.1 }) → € per unit ({ INR: 0.01086 }). */
function invert(perEur: Record<string, number>): RatesToEur {
  const out: RatesToEur = { EUR: 1 };
  for (const [c, v] of Object.entries(perEur)) if (typeof v === 'number' && v > 0) out[c.toUpperCase()] = 1 / v;
  return out;
}

export async function fetchLiveRates(today: string): Promise<LiveRates | null> {
  try {
    const cached = JSON.parse(localStorage.getItem(LIVE_RATES_KEY) ?? 'null') as LiveRates | null;
    if (cached?.date === today && cached.rates) return cached;
  } catch { /* no storage */ }
  let live: LiveRates | null = null;
  try {
    const r = await fetch('https://open.er-api.com/v6/latest/EUR');
    const j = await r.json() as { result?: string; rates?: Record<string, number> };
    if (j.result === 'success' && j.rates) live = { date: today, rates: invert(j.rates) };
  } catch { /* try the mirror */ }
  if (!live) {
    try {
      const r = await fetch('https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/eur.json');
      const j = await r.json() as { eur?: Record<string, number> };
      if (j.eur) live = { date: today, rates: invert(j.eur) };
    } catch { /* offline: fallback rates */ }
  }
  if (live) { try { localStorage.setItem(LIVE_RATES_KEY, JSON.stringify(live)); } catch { /* private window */ } }
  return live;
}

// Defaults are the EUR plan (kept for older code / companies).
export const DEFAULT_FIRST_YEAR_AMOUNT = PLAN_EUR.firstYear;
export const DEFAULT_RENEWAL_AMOUNT    = PLAN_EUR.renewal;
export const DEFAULT_INCLUDED_USERS    = 3;
export const DEFAULT_EXTRA_USER_RATE   = PLAN_EUR.extraUser;

type Pricing = Pick<Business, 'firstYearAmount' | 'baseAmount' | 'extraUserRate' | 'includedUsers' | 'billingCurrency'>;

export function includedUsersOf(b: Pick<Business, 'includedUsers'>): number {
  return b.includedUsers != null && b.includedUsers > 0 ? b.includedUsers : DEFAULT_INCLUDED_USERS;
}
// A price not set on the company falls back to the plan in ITS currency
// (at the fallback rates; every company saved from the form has its own).
const fallbackPlan = (b: Pricing) => planFor(billingCurrencyOf(b), DEFAULT_RATES_TO_EUR);
export const firstYearAmountOf = (b: Pricing) => b.firstYearAmount ?? fallbackPlan(b).firstYear;
export const renewalAmountOf   = (b: Pricing) => b.baseAmount      ?? fallbackPlan(b).renewal;
export const extraUserRateOf   = (b: Pricing) => b.extraUserRate   ?? fallbackPlan(b).extraUser;

export type BillingStage = 'first-year' | 'renewal';

/** A company is past its first year once it has any First Year or Renewal
 *  payment. Older payments without a type count too (they were paid before
 *  payment types existed). */
export function stageFor(businessId: string, payments: Pick<Payment, 'businessId' | 'kind'>[]): BillingStage {
  const paid = payments.some(p => p.businessId === businessId
    && (p.kind === 'first-year' || p.kind === 'renewal' || p.kind == null));
  return paid ? 'renewal' : 'first-year';
}

export interface Billing {
  currency:      BillingCurrency;
  stage:         BillingStage;
  activeUsers:   number;
  included:      number;
  extraUsers:    number;
  extraUserRate: number;
  feeAmt:        number;   // first year or renewal fee
  extraAmt:      number;
  totalDue:      number;
}

/** What a company owes for its next payment (first year or renewal). */
export function billingFor(b: Pricing, activeUsers: number, stage: BillingStage): Billing {
  const included      = includedUsersOf(b);
  const extraUsers    = Math.max(0, activeUsers - included);
  const extraUserRate = extraUserRateOf(b);
  const feeAmt        = stage === 'first-year' ? firstYearAmountOf(b) : renewalAmountOf(b);
  const extraAmt      = extraUsers * extraUserRate;
  return { currency: billingCurrencyOf(b), stage, activeUsers, included, extraUsers, extraUserRate, feeAmt, extraAmt, totalDue: feeAmt + extraAmt };
}

/** The frozen breakdown stored on a First Year / Renewal payment. */
export function paymentBreakdown(bill: Billing) {
  return {
    feeAmount:     bill.feeAmt,
    includedUsers: bill.included,
    extraUsers:    bill.extraUsers,
    extraUserRate: bill.extraUserRate,
    extraAmount:   bill.extraAmt,
  };
}

export const PAYMENT_KIND_LABEL: Record<PaymentKind, string> = {
  'first-year':  'First Year',
  'renewal':     'Renewal',
  'extra-users': 'Extra Users',
  'other':       'Other',
};
export const STAGE_LABEL: Record<BillingStage, string> = {
  'first-year': 'First year',
  'renewal':    'Renewal',
};
